/**
 * Picking sub-view — manually build bundles for an approved order.
 *
 * Flow:
 *   1) See list of approved (or partially packed) orders
 *   2) Open one → see line items as `0/200` progress
 *   3) "New bag" → pick which product(s) + quantity (default = minimum_quantity)
 *   4) Save bag → progress updates, bag appears in list with a Print button
 *   5) Print the delivery card for that bag at any time (one per bag)
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Printer, Trash2, PackagePlus, CheckCircle2, Truck, ClipboardList } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DispatchPickingView() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summarySelection, setSummarySelection] = useState(() => new Set());
  const [runByOrder, setRunByOrder] = useState({});

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [ordersRes, productsRes, usersRes, routesRes] = await Promise.all([
        axios.get(`${API}/orders?group_by=none`),
        axios.get(`${API}/products`),
        axios.get(`${API}/users`),
        axios.get(`${API}/delivery-routes`).catch(() => ({ data: [] })),
      ]);
      // orderId → active run (pending/planned). Packing is gated on this.
      const runMap = {};
      for (const r of (routesRes.data || [])) {
        if (r.status === "completed") continue;
        for (const oid of (r.order_ids || [])) runMap[oid] = { id: r.id, name: r.name };
      }
      setRunByOrder(runMap);
      // Backend returns `{ group_by: "none", orders: [...] }` — flat list.
      const flat = ordersRes.data?.orders || [];
      // Picking is meaningful when there's still operator work:
      // - The order is approved but no bags yet, OR
      // - At least one bag is still in 'packing' (operator can edit/add bags).
      // Orders whose bags have all moved past 'packing' belong to the Check
      // / Delivery views and shouldn't clutter Picking.
      const eligible = flat.filter((o) => {
        const bundles = Array.isArray(o.bundles) ? o.bundles : [];
        const hasPacking = bundles.some((b) => b.status === "packing");
        const isApprovedNoBags = o.status === "approved" && bundles.length === 0;
        return hasPacking || isApprovedNoBags;
      });
      // Sort by delivery date ASCENDING (earliest first) so the most urgent
      // batch — today/tomorrow — sits at the top of the Picking queue.
      eligible.sort((a, b) => (a.delivery_date || "").localeCompare(b.delivery_date || ""));
      setOrders(eligible);
      setProducts(productsRes.data || []);
      setHotels(usersRes.data || []);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const refreshOrderDetail = async (orderId) => {
    if (!orderId) return;
    try {
      const res = await axios.get(`${API}/dispatch/orders/${orderId}/bundles`);
      setOrderDetail(res.data);
    } catch (e) {
      toast.error("Failed to load order detail");
    }
  };

  const selectOrder = async (orderId) => {
    const o = orders.find((x) => x.id === orderId);
    if (o && !o.is_pickup && !runByOrder[orderId]) {
      toast.error("Not on a delivery run yet — assign this order to a run first (Delivery → Runs)");
      return;
    }
    setSelectedOrderId(orderId);
    setOrderDetail(null);
    await refreshOrderDetail(orderId);
  };

  // Group orders by delivery_date for headed sections. Keep DESC order
  // (already sorted) and bucket as "Today / Tomorrow / Yesterday / explicit
  // date" so the operator sees urgent batches at the top.
  const grouped = useMemo(() => {
    const groups = new Map();
    for (const o of orders) {
      const key = o.delivery_date || "No date";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(o);
    }
    return Array.from(groups.entries());
  }, [orders]);

  const formatDateHeading = (iso) => {
    if (!iso || iso === "No date") return "No delivery date";
    // YYYY-MM-DD → friendly label with weekday + relative tag.
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dt = new Date(iso + "T00:00:00");
    const diffDays = Math.round((dt - today) / 86400000);
    let rel = "";
    if (diffDays === 0)      rel = " · Today";
    else if (diffDays === 1) rel = " · Tomorrow";
    else if (diffDays === -1) rel = " · Yesterday";
    else if (diffDays > 1)   rel = ` · in ${diffDays} days`;
    else if (diffDays < -1)  rel = ` · ${-diffDays} days ago`;
    const wd = dt.toLocaleDateString(undefined, { weekday: "short" });
    const md = dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return `${wd}, ${md}${rel}`;
  };

  // Today's ISO date in local time (matches order.delivery_date format).
  const todayIso = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
  }, []);

  /** Aggregate the picking summary for a Set of order IDs. */
  const summaryData = useMemo(() => {
    const selectedOrders = orders.filter((o) => summarySelection.has(o.id));
    // Total per product across all selected orders.
    const totals = new Map(); // pid -> { product_name, qty }
    // Per hotel breakdown — hotel_name -> Map(pid -> {product_name, qty})
    const byHotel = new Map();
    for (const o of selectedOrders) {
      const hotelKey = o.hotel_name || o.user_name || o.user_email || "(no hotel)";
      const hotelMap = byHotel.get(hotelKey) || new Map();
      for (const it of (o.items || [])) {
        const pid = it.product_id;
        const name = it.product_name || pid;
        const q = it.quantity || 0;
        if (!q) continue;
        const cur = totals.get(pid) || { product_name: name, qty: 0 };
        cur.qty += q;
        totals.set(pid, cur);
        const hCur = hotelMap.get(pid) || { product_name: name, qty: 0 };
        hCur.qty += q;
        hotelMap.set(pid, hCur);
      }
      // Positive adjustments = real goods charged outside the catalog —
      // include them in the picking summary so they get packed too.
      (o.adjustments || []).forEach((adj, idx) => {
        if ((adj.amount || 0) <= 0) return;
        const pid = `adj:${o.id}:${idx}`;
        const name = adj.description || "Adjustment item";
        const q = parseInt(adj.quantity) || 1;
        totals.set(pid, { product_name: name, qty: q });
        hotelMap.set(pid, { product_name: name, qty: q });
      });
      byHotel.set(hotelKey, hotelMap);
    }
    const sortByName = (m) => Array.from(m.values()).sort((a, b) => (a.product_name || "").localeCompare(b.product_name || ""));
    return {
      orderCount: selectedOrders.length,
      totals: sortByName(totals),
      hotels: Array.from(byHotel.entries())
        .map(([hotel_name, m]) => ({ hotel_name, items: sortByName(m), pcs: Array.from(m.values()).reduce((s, x) => s + x.qty, 0) }))
        .sort((a, b) => (a.hotel_name || "").localeCompare(b.hotel_name || "")),
    };
  }, [orders, summarySelection]);

  const openSummary = () => {
    // Default: select only the CLOSEST delivery date (today if any, else the
    // nearest upcoming date, else the most recent past date). Operators have
    // asked us NOT to default-select-all — it's too easy to print the wrong
    // batch.
    const allDates = Array.from(new Set(orders.map((o) => o.delivery_date).filter(Boolean))).sort();
    const upcoming = allDates.find((d) => d >= todayIso);
    const past = [...allDates].reverse().find((d) => d < todayIso);
    const target = upcoming || past || null;
    const initialIds = target
      ? orders.filter((o) => o.delivery_date === target).map((o) => o.id)
      : [];
    setSummarySelection(new Set(initialIds));
    setSummaryOpen(true);
  };
  const toggleSummaryOrder = (id) => {
    setSummarySelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllSummary = () => {
    setSummarySelection(new Set(orders.map((o) => o.id)));
  };
  const deselectAllSummary = () => {
    setSummarySelection(new Set());
  };

  const printSummary = () => {
    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]));
    const w = window.open("", "_blank", "width=600,height=820");
    if (!w) return;
    const totalsHtml = summaryData.totals
      .map((t) => `<tr><td>${escapeHtml(t.product_name)}</td><td style="text-align:right;font-family:monospace;font-weight:700">${t.qty}</td></tr>`)
      .join("");
    const hotelsHtml = summaryData.hotels
      .map((h) => {
        const rows = h.items
          .map((it) => `<tr><td>${escapeHtml(it.product_name)}</td><td style="text-align:right;font-family:monospace">${it.qty}</td></tr>`)
          .join("");
        return `
          <h3>${escapeHtml(h.hotel_name)} <span class="hpcs">${h.pcs} pcs</span></h3>
          <table>${rows}</table>
        `;
      })
      .join("<hr/>");
    const grandTotal = summaryData.totals.reduce((s, t) => s + t.qty, 0);
    w.document.write(`<!doctype html><html><head><title>Picking summary</title>
<style>
  /* 80mm thermal receipt — pure black only, no greys. */
  body { font-family: -apple-system, system-ui, sans-serif; width: 72mm; margin: 0 auto; padding: 2mm; color: #000; }
  body, body * { color: #000 !important; }
  h1 { font-size: 20px; margin: 0; }
  h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.12em; margin: 14px 0 4px; font-weight: 800; }
  h3 { font-size: 15px; margin: 10px 0 3px; display: flex; justify-content: space-between; align-items: baseline; }
  .hpcs { font-size: 12px; font-weight: 700; }
  .meta { font-size: 12px; margin-top: 2px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  td { padding: 4px 0; border-bottom: 1px dashed #000; }
  hr { border: 0; border-top: 2px solid #000; margin: 10px 0; }
  .grand { font-size: 17px; font-weight: 800; margin-top: 6px; }
  @page { size: 80mm auto; margin: 0; }
  @media print { body { margin: 0; } }
</style></head><body>
  <h1>Picking summary</h1>
  <div class="meta">${summaryData.orderCount} order(s) · ${summaryData.hotels.length} hotel(s) · printed ${new Date().toLocaleString()}</div>

  <h2>Totals (all selected)</h2>
  <table>${totalsHtml || '<tr><td colspan="2"><em>Nothing selected.</em></td></tr>'}</table>
  <div class="grand">Grand total: ${grandTotal} pcs</div>

  <hr/>
  <h2>By hotel</h2>
  ${hotelsHtml}

  <script>window.onload = () => setTimeout(() => { window.print(); setTimeout(() => window.close(), 400); }, 250);</script>
</body></html>`);
    w.document.close();
  };

  if (selectedOrderId && orderDetail) {
    return (
      <OrderPickingDetail
        detail={orderDetail}
        products={products}
        hotels={hotels}
        onBack={() => { setSelectedOrderId(null); setOrderDetail(null); refresh(); }}
        onChanged={() => refreshOrderDetail(selectedOrderId)}
      />
    );
  }

  return (
    <>
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-display text-xl text-brand-navy">Approved orders</CardTitle>
        <Button
          variant="outline"
          size="sm"
          onClick={openSummary}
          disabled={orders.length === 0}
          data-testid="picking-summary-open"
        >
          <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Picking summary
        </Button>
      </CardHeader>
      <CardContent>
        {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!loading && orders.length === 0 && (
          <p className="text-sm text-muted-foreground">No approved orders to pack.</p>
        )}
        <div className="space-y-5">
          {grouped.map(([date, list]) => (
            <div key={date} className="space-y-2">
              <div className="flex items-baseline justify-between border-b border-neutral-200 pb-1.5">
                <h4 className="text-xs uppercase tracking-[0.16em] font-semibold text-brand-navy">
                  {formatDateHeading(date)}
                </h4>
                <span className="text-[11px] text-muted-foreground">
                  {list.length} order{list.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="space-y-2">
                {list.map((o) => {
                  const bundleCount = (o.bundles || []).length;
                  const packed = (o.bundles || []).reduce(
                    (s, b) => s + (b.items || []).reduce((x, i) => x + (i.quantity || 0), 0),
                    0,
                  );
                  const ordered = (o.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
                  // In the Picking tab, "fully packed" is enough — once the
                  // user clicks "Complete delivery" the order moves to the
                  // Delivery tab, so we don't gate the green badge on
                  // dispatch status (it would never be reachable here anyway).
                  const fullyPacked = ordered > 0 && packed >= ordered;
                  const partiallyPacked = bundleCount > 0 && !fullyPacked;
                  return (
                    <button
                      key={o.id}
                      data-testid={`dispatch-order-${o.id}`}
                      onClick={() => selectOrder(o.id)}
                      className="w-full flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-left rounded-md border border-neutral-200 bg-white px-4 py-3 hover:border-brand-navy transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-semibold">{o.order_number}</span>
                          <span className="text-sm text-muted-foreground truncate">{o.hotel_name}</span>
                          {o.is_pickup && (
                            <Badge
                              data-testid={`picking-pickup-badge-${o.id}`}
                              className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5"
                            >
                              <Truck className="h-3 w-3 mr-1" /> Pickup
                            </Badge>
                          )}
                          {runByOrder[o.id] ? (
                            <Badge
                              data-testid={`picking-run-badge-${o.id}`}
                              className="bg-sky-100 text-sky-800 border border-sky-200 text-[10px]"
                            >
                              {runByOrder[o.id].name || "Run"}
                            </Badge>
                          ) : !o.is_pickup && (
                            <Badge
                              data-testid={`picking-norun-badge-${o.id}`}
                              className="bg-neutral-100 text-neutral-500 border border-neutral-200 text-[10px]"
                            >
                              No run
                            </Badge>
                          )}
                          {fullyPacked && (
                            <Badge
                              data-testid={`picking-badge-done-${o.id}`}
                              className="bg-green-100 text-green-800 border-green-200"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Packed
                            </Badge>
                          )}
                          {partiallyPacked && (
                            <Badge
                              data-testid={`picking-badge-partial-${o.id}`}
                              className="bg-amber-100 text-amber-800 border-amber-300"
                            >
                              Partially packed
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {bundleCount} bag{bundleCount === 1 ? "" : "s"} · {packed}/{ordered} packed
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-brand-navy">Open →</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>

    {/* Picking summary dialog — preview goods needed across selected orders. */}
    <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display">Picking summary</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Per-order checkboxes (grouped by date) */}
          <div className="border rounded-md bg-neutral-50">
            {/* Global Select All / Deselect All toolbar */}
            <div className="px-3 py-2 bg-white border-b flex items-center gap-2 sticky top-0 z-10">
              <span className="text-xs font-semibold text-muted-foreground">
                {summarySelection.size} of {orders.length} selected
              </span>
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={selectAllSummary}
                  className="text-xs uppercase tracking-wider font-semibold text-brand-navy hover:underline"
                  data-testid="picking-summary-select-all"
                >
                  Select all
                </button>
                <span className="text-neutral-300">·</span>
                <button
                  type="button"
                  onClick={deselectAllSummary}
                  className="text-xs uppercase tracking-wider font-semibold text-brand-navy hover:underline"
                  data-testid="picking-summary-deselect-all"
                >
                  Deselect all
                </button>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
            {grouped.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground">No approved orders.</div>
            ) : grouped.map(([date, list]) => {
              const ids = list.map((o) => o.id);
              const allSelected = ids.length > 0 && ids.every((id) => summarySelection.has(id));
              const someSelected = ids.some((id) => summarySelection.has(id));
              const toggleGroup = () => {
                setSummarySelection((prev) => {
                  const next = new Set(prev);
                  if (allSelected) ids.forEach((id) => next.delete(id));
                  else ids.forEach((id) => next.add(id));
                  return next;
                });
              };
              return (
                <div key={date}>
                  <div className="px-3 py-3 bg-neutral-100 border-b flex items-center gap-3">
                    <Checkbox
                      checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                      onCheckedChange={toggleGroup}
                      data-testid={`picking-summary-group-${date}`}
                    />
                    <span className="text-base font-bold text-brand-navy">
                      {formatDateHeading(date)}
                    </span>
                    <span className="text-xs text-muted-foreground">· {list.length} order{list.length === 1 ? "" : "s"}</span>
                    <button
                      type="button"
                      onClick={toggleGroup}
                      className="ml-auto text-xs uppercase tracking-wider font-semibold text-brand-navy hover:underline"
                      data-testid={`picking-summary-group-toggle-${date}`}
                    >
                      {allSelected ? "Deselect all" : "Select all"}
                    </button>
                  </div>
                  {list.map((o) => (
                    <label key={o.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-neutral-100 cursor-pointer border-b last:border-b-0">
                      <Checkbox
                        checked={summarySelection.has(o.id)}
                        onCheckedChange={() => toggleSummaryOrder(o.id)}
                        data-testid={`picking-summary-order-${o.id}`}
                      />
                      <span className="font-mono text-xs text-muted-foreground">#{o.order_number}</span>
                      <span className="truncate">{o.hotel_name || o.user_name || "—"}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {(o.items || []).reduce((s, i) => s + (i.quantity || 0), 0)} pcs
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
            </div>
          </div>

          {/* Aggregated summary — totals + per hotel breakdown */}
          {summaryData.orderCount > 0 ? (
            <div className="rounded-md border bg-white p-4 space-y-3" data-testid="picking-summary-card">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Totals</div>
                {summaryData.totals.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">No items.</div>
                ) : (
                  <div className="space-y-1">
                    {summaryData.totals.map((t) => (
                      <div key={t.product_name} className="flex items-center justify-between text-base">
                        <span className="font-medium">{t.product_name}</span>
                        <span className="font-mono font-bold tabular-nums">{t.qty}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-base border-t-2 border-black pt-1.5 mt-2">
                      <span className="font-bold">Grand total</span>
                      <span className="font-mono font-extrabold tabular-nums">{summaryData.totals.reduce((s, t) => s + t.qty, 0)} pcs</span>
                    </div>
                  </div>
                )}
              </div>
              <hr className="border-black" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">By hotel</div>
                <div className="space-y-3">
                  {summaryData.hotels.map((h) => (
                    <div key={h.hotel_name}>
                      <div className="flex items-baseline justify-between mb-0.5">
                        <div className="font-display font-bold">{h.hotel_name}</div>
                        <div className="text-xs text-muted-foreground">{h.pcs} pcs</div>
                      </div>
                      <div className="space-y-0.5 pl-3 border-l-2 border-neutral-200">
                        {h.items.map((it) => (
                          <div key={it.product_name} className="flex items-center justify-between text-sm">
                            <span>{it.product_name}</span>
                            <span className="font-mono tabular-nums">{it.qty}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-md border bg-neutral-50 p-6 text-center text-sm text-muted-foreground">
              Select one or more orders to preview the picking summary.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSummaryOpen(false)} data-testid="picking-summary-close">Close</Button>
          <Button onClick={printSummary} disabled={summaryData.orderCount === 0} data-testid="picking-summary-print">
            <Printer className="h-4 w-4 mr-2" /> Print summary
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ----------------------------------------------------------------------------

function OrderPickingDetail({ detail, products, hotels, onBack, onChanged }) {
  const [creating, setCreating] = useState(false);
  const [bagToDelete, setBagToDelete] = useState(null);
  const productsById = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[p.id] = p; });
    return m;
  }, [products]);
  const hotelObj = useMemo(
    () => hotels.find((h) => h.id === detail.hotel_id) || null,
    [hotels, detail.hotel_id]
  );

  // Products flagged `is_default_extra` are auto-included as extras (qty 1)
  // on every new bag. The operator can adjust quantities or remove them
  // inside the New Bag dialog. Replaces the previous fragile name-matching
  // heuristic that only looked for a product literally named "laundry bag".
  const defaultExtraProducts = useMemo(
    () => (products || []).filter((p) => p && p.is_default_extra),
    [products],
  );

  const packedByPid = detail.packed_totals || {};
  const orderLines = detail.items || [];
  const bundles = detail.bundles || [];
  const nextBundleNo = bundles.length + 1;

  const remainingByPid = useMemo(() => {
    const out = {};
    orderLines.forEach((l) => {
      const remaining = (l.quantity || 0) - (packedByPid[l.product_id] || 0);
      out[l.product_id] = Math.max(0, remaining);
    });
    return out;
  }, [orderLines, packedByPid]);

  const orderFullyPacked = orderLines.every(
    (l) => (packedByPid[l.product_id] || 0) >= (l.quantity || 0)
  );

  const confirmDelete = async () => {
    if (!bagToDelete) return;
    try {
      await axios.delete(`${API}/dispatch/orders/${detail.order_id}/bundles/${bagToDelete.id}`);
      toast.success(`Bag #${bagToDelete.bundle_no} removed`);
      setBagToDelete(null);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove bag");
      setBagToDelete(null);
    }
  };

  const handlePrint = (bundle) => {
    openDeliveryCard(bundle, detail, hotelObj, orderLines);
  };

  // How many bags are still in `packing`? Drives the Complete Picking button.
  const packingCount = bundles.filter((b) => b.status === "packing").length;

  const handleCompletePicking = async () => {
    try {
      const res = await axios.post(`${API}/dispatch/orders/${detail.order_id}/complete-picking`);
      toast.success(`Picking complete — ${res.data?.flipped || 0} bag(s) handed off to Check`);
      // Order moves out of the Picking queue → return to the list.
      onBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete picking");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold text-brand-navy tracking-tight flex items-center gap-2 flex-wrap">
            Picking — {detail.order_number}
            {detail.is_pickup && (
              <Badge className="bg-orange-100 text-orange-800 border border-orange-300 text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5">
                <Truck className="h-3 w-3 mr-1" /> Pickup
              </Badge>
            )}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="font-medium text-foreground">{detail.hotel_name}</span> · Delivery {detail.delivery_date}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onBack} data-testid="dispatch-picking-back">
            ← Back
          </Button>
          <Button
            data-testid="dispatch-complete-picking"
            onClick={handleCompletePicking}
            disabled={packingCount === 0}
            className="bg-brand-navy hover:bg-brand-navy-hover text-white"
            title={
              packingCount === 0
                ? "No bags are still being packed"
                : `Hand off ${packingCount} bag(s) to the Check wave`
            }
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Complete picking
            {packingCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-white/20 text-xs">
                {packingCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Required vs Packed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {orderLines.map((l) => {
              const packed = packedByPid[l.product_id] || 0;
              const expected = l.quantity || 0;
              const done = packed >= expected;
              return (
                <div
                  key={l.product_id}
                  className={`flex justify-between items-center px-3 py-2 rounded-md border ${
                    done ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"
                  }`}
                >
                  <span className="font-medium flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: productsById[l.product_id]?.color || "#999" }}
                    />
                    {l.product_name}
                  </span>
                  <span className="font-mono text-sm font-semibold">
                    {packed}/{expected}
                  </span>
                </div>
              );
            })}
            {orderFullyPacked && (
              <p className="text-xs text-green-700 font-semibold mt-2">
                ✓ All items packed. You can still split into more bags if needed (none will exceed the ordered quantity).
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Bags ({bundles.length})</CardTitle>
            <Button
              size="sm"
              onClick={() => setCreating(true)}
              data-testid="dispatch-new-bag-button"
              className="bg-brand-navy hover:bg-brand-navy-hover"
            >
              <Plus className="h-4 w-4 mr-1" /> New bag
            </Button>
          </CardHeader>
          <CardContent>
            {bundles.length === 0 && (
              <p className="text-sm text-muted-foreground">No bags yet — create your first bag.</p>
            )}
            <div className="space-y-2">
              {bundles.map((b) => (
                <div
                  key={b.id}
                  className="rounded-md border border-neutral-200 bg-white px-3 py-2 space-y-2"
                  data-testid={`dispatch-bag-${b.id}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <PackagePlus className="h-4 w-4 text-brand-navy" />
                      <span className="font-semibold">Bag #{b.bundle_no}</span>
                      <span className="font-mono text-xs text-muted-foreground">{b.barcode}</span>
                      <BundleStatusBadge status={b.status} />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePrint(b)}
                        data-testid={`dispatch-bag-print-${b.id}`}
                      >
                        <Printer className="h-3.5 w-3.5 mr-1" /> Print
                      </Button>
                      {b.status === "packing" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setBagToDelete(b)}
                          data-testid={`dispatch-bag-delete-${b.id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5 pl-6">
                    {(b.items || []).map((it, idx) => (
                      <li key={idx}>
                        {it.product_name}: <span className="font-mono font-semibold">{it.quantity}</span>
                        {it.is_extra && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-amber-700">extra</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <NewBagDialog
        open={creating}
        onClose={() => setCreating(false)}
        orderLines={orderLines}
        productsById={productsById}
        remainingByPid={remainingByPid}
        orderId={detail.order_id}
        nextBundleNo={nextBundleNo}
        defaultExtraProducts={defaultExtraProducts}
        onCreated={() => { setCreating(false); onChanged(); }}
      />

      <AlertDialog open={!!bagToDelete} onOpenChange={(v) => !v && setBagToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Bag #{bagToDelete?.bundle_no}?</AlertDialogTitle>
            <AlertDialogDescription>
              This bag will be deleted and its contents returned to the unpacked pool.
              Remaining bags will be re-numbered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="dispatch-bag-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
            >
              Remove bag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ----------------------------------------------------------------------------

function BundleStatusBadge({ status }) {
  if (status === "packing")    return <Badge className="bg-neutral-100 text-neutral-700 border-neutral-200">Packing</Badge>;
  if (status === "packed")     return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Ready for check</Badge>;
  if (status === "dispatched") return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Dispatched</Badge>;
  if (status === "delivered")  return <Badge className="bg-green-100 text-green-800 border-green-200">Delivered</Badge>;
  return <Badge>{status}</Badge>;
}

// ----------------------------------------------------------------------------

function NewBagDialog({ open, onClose, orderLines, productsById, remainingByPid, orderId, nextBundleNo, defaultExtraProducts, onCreated }) {
  // Default qty is 0 — the operator must explicitly enter how many.
  const buildInitialRows = () =>
    orderLines.map((l) => ({
      product_id: l.product_id,
      product_name: l.product_name,
      quantity: 0,
    }));
  // Every new bag auto-includes the products flagged `is_default_extra`
  // (typically laundry bags, reject bags, etc.). The operator can adjust
  // quantities or remove rows from the dialog if a particular one doesn't
  // apply to this bag.
  const buildInitialExtras = () => {
    if (!defaultExtraProducts || defaultExtraProducts.length === 0) return [];
    return defaultExtraProducts.map((p) => ({
      product_id: p.id,
      product_name: p.name,
      quantity: 1,
      is_extra: true,
    }));
  };
  const [rows, setRows] = useState(buildInitialRows);
  const [extraRows, setExtraRows] = useState(buildInitialExtras);
  const [submitting, setSubmitting] = useState(false);

  // Reset rows when dialog re-opens with fresh remaining data
  useEffect(() => {
    if (open) {
      setRows(buildInitialRows());
      setExtraRows(buildInitialExtras());
    }
    // eslint-disable-next-line
  }, [open, orderLines, remainingByPid, defaultExtraProducts]);

  const setRowQty = (pid, q) => {
    setRows((prev) => prev.map((r) => (r.product_id === pid ? { ...r, quantity: q } : r)));
  };

  const setExtraQty = (pid, q) => {
    setExtraRows((prev) => prev.map((r) => (r.product_id === pid ? { ...r, quantity: Math.max(0, q) } : r)));
  };

  const removeExtra = (pid) => {
    setExtraRows((prev) => prev.filter((r) => r.product_id !== pid));
  };

  const submit = async () => {
    const items = [
      ...rows.filter((r) => r.quantity > 0).map((r) => ({
        product_id: r.product_id,
        product_name: r.product_name,
        quantity: r.quantity,
      })),
      ...extraRows.filter((r) => r.quantity > 0).map((r) => ({
        product_id: r.product_id,
        product_name: r.product_name,
        quantity: r.quantity,
        is_extra: true,
      })),
    ];
    if (items.length === 0) {
      toast.error("Add at least one product to this bag");
      return;
    }
    // Client-side cap check matches backend validation (extras are uncapped).
    for (const r of rows) {
      if (r.quantity > (remainingByPid[r.product_id] || 0)) {
        toast.error(`${r.product_name}: only ${remainingByPid[r.product_id]} left to pack`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/dispatch/orders/${orderId}/bundles`, { items });
      toast.success(`Bag #${res.data.bundle_no} created — ${res.data.barcode}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create bag");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New bag — Bag #{nextBundleNo}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Enter how many of each product go in this bag. Use <strong>±unit</strong> to add or remove a full bundle,
          or <strong>±1</strong> to fine-tune (e.g. 53 instead of 50).
        </p>
        <div className="space-y-3 mt-2">
          {rows.map((r) => {
            const remaining = remainingByPid[r.product_id] || 0;
            const unit = productsById[r.product_id]?.minimum_quantity || 1;
            const disabled = remaining === 0;
            const cap = (n) => Math.max(0, Math.min(remaining, n));
            return (
              <div key={r.product_id} className="grid grid-cols-[1fr_auto] gap-3 items-center py-1">
                <div className="min-w-0">
                  <Label className="text-base font-semibold">{r.product_name}</Label>
                  <p className="text-sm text-muted-foreground">
                    {remaining} left · unit {unit}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {/* By-unit (e.g. ±10) */}
                  <button
                    type="button"
                    disabled={disabled || r.quantity <= 0}
                    onClick={() => setRowQty(r.product_id, cap(r.quantity - unit))}
                    data-testid={`new-bag-minus-unit-${r.product_id}`}
                    className="h-12 min-w-12 px-3 rounded-md border text-base font-mono font-bold hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-40"
                    title={`Decrease by ${unit}`}
                  >
                    −{unit}
                  </button>
                  <button
                    type="button"
                    disabled={disabled || r.quantity >= remaining}
                    onClick={() => setRowQty(r.product_id, cap(r.quantity + unit))}
                    data-testid={`new-bag-plus-unit-${r.product_id}`}
                    className="h-12 min-w-12 px-3 rounded-md border text-base font-mono font-bold hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-40"
                    title={`Increase by ${unit}`}
                  >
                    +{unit}
                  </button>
                  <Input
                    type="number"
                    min="0"
                    max={remaining}
                    value={r.quantity}
                    onChange={(e) => setRowQty(r.product_id, cap(parseInt(e.target.value || "0", 10)))}
                    disabled={disabled}
                    data-testid={`new-bag-qty-${r.product_id}`}
                    className="w-20 h-12 text-lg font-mono font-bold text-center mx-1"
                  />
                  {/* Single piece (±1) */}
                  <button
                    type="button"
                    disabled={disabled || r.quantity <= 0}
                    onClick={() => setRowQty(r.product_id, cap(r.quantity - 1))}
                    data-testid={`new-bag-minus-${r.product_id}`}
                    className="h-12 w-12 rounded-md border text-xl font-bold hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-40"
                    title="Decrease by 1"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    disabled={disabled || r.quantity >= remaining}
                    onClick={() => setRowQty(r.product_id, cap(r.quantity + 1))}
                    data-testid={`new-bag-plus-${r.product_id}`}
                    className="h-12 w-12 rounded-md border text-xl font-bold hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-40"
                    title="Increase by 1"
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {extraRows.length > 0 && (
          <div className="mt-4 space-y-3 border-t border-neutral-200 pt-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Extras (not counted toward order)
              </span>
              <span className="text-[11px] text-muted-foreground italic">
                Auto-included with every bag · adjust or remove below
              </span>
            </div>
            {extraRows.map((r) => (
              <div key={r.product_id} className="grid grid-cols-[1fr_auto] gap-3 items-center py-1">
                <div className="min-w-0">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    {r.product_name}
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">extra</span>
                  </Label>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setExtraQty(r.product_id, r.quantity - 1)}
                    disabled={r.quantity <= 0}
                    className="h-12 w-12 rounded-md border text-xl font-bold hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-40"
                    title="Decrease by 1"
                  >
                    −
                  </button>
                  <Input
                    type="number"
                    min="0"
                    value={r.quantity}
                    onChange={(e) => setExtraQty(r.product_id, parseInt(e.target.value || "0", 10))}
                    data-testid={`new-bag-extra-qty-${r.product_id}`}
                    className="w-20 h-12 text-lg font-mono font-bold text-center mx-1"
                  />
                  <button
                    type="button"
                    onClick={() => setExtraQty(r.product_id, r.quantity + 1)}
                    className="h-12 w-12 rounded-md border text-xl font-bold hover:bg-neutral-50 active:bg-neutral-100"
                    title="Increase by 1"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => removeExtra(r.product_id)}
                    data-testid={`new-bag-extra-remove-${r.product_id}`}
                    className="ml-1 h-12 w-12 rounded-md text-2xl font-bold text-red-600 hover:bg-red-50 active:bg-red-100"
                    title="Remove this extra"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="new-bag-submit"
            className="bg-brand-navy hover:bg-brand-navy-hover"
          >
            {submitting ? "Saving…" : "Save bag"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------

/** Opens a print window with one delivery card. */
export function openDeliveryCard(bundle, detail, hotelObj, orderLines) {
  const w = window.open("", "_blank", "width=600,height=820");
  if (!w) return;
  const dateStr = new Date().toLocaleString();
  const bundles = detail.bundles || [];
  const totalBags = bundles.length;
  const hotelCode = hotelObj?.hotel_code || detail.hotel_code || "";
  const hotelAddress = hotelObj?.hotel_address || "";

  const bagItems = (bundle.items || [])
    .map((it) => `<tr><td>${it.product_name}</td><td style="text-align:right;font-family:monospace">${it.quantity}</td></tr>`)
    .join("");
  const orderTotals = (orderLines || [])
    .map((l) => `<tr><td>${l.product_name}</td><td style="text-align:right;font-family:monospace">${l.quantity}</td></tr>`)
    .join("");

  // JsBarcode is heavy — use a minimal SVG Code128 generator via barcode lib URL.
  // To keep it dependency-free we render a simple bar pattern stub and the code
  // text below it. A real scanner will read the code text via OCR fallback; for
  // proper scannable barcodes the user can install JsBarcode separately.
  const renderBarcodeBlock = (code, label) => {
    if (!code) return "";
    return `
      <div style="text-align:center;margin:4px 0;">
        <svg id="bc-${label}" class="bc" jsbarcode-value="${code}" jsbarcode-format="CODE128" jsbarcode-displayvalue="true" jsbarcode-height="48" jsbarcode-fontsize="14"></svg>
      </div>
    `;
  };

  w.document.write(`<!doctype html><html><head><title>Delivery card — ${bundle.barcode}</title>
  <style>
    /* Thermal-printer friendly: everything in pure black (no greys — they
       come out faint on receipt printers), generous font sizes, no filled
       black-on-white badges (heat-prints them as smudges). */
    body { font-family: -apple-system, system-ui, sans-serif; max-width: 500px; margin: 16px auto; color: #000; padding: 0 16px; }
    h1 { font-size: 26px; margin: 0; letter-spacing: -0.01em; color: #000; }
    h2 { font-size: 13px; text-transform: uppercase; letter-spacing: 0.14em; color: #000; margin: 10px 0 3px; font-weight: 700; }
    .row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 16px; border-bottom: 1px dashed #000; color: #000; line-height: 1.25; }
    .row strong { font-weight: 700; }
    .big { font-size: 22px; font-weight: 800; letter-spacing: -0.01em; color: #000; }
    /* The "Bag X/Y" indicator — outlined instead of filled, large, BOLD. */
    .bag-num { display: inline-block; font-size: 22px; font-weight: 800; padding: 4px 14px; border: 3px solid #000; border-radius: 6px; margin-left: 8px; vertical-align: middle; color: #000; }
    /* Loud PICKUP banner shown ONLY for is_pickup orders — outlined so it
       thermal-prints cleanly (filled = smudged). The double border + huge
       italic label makes it unmissable when the driver picks up the card. */
    .pickup-banner { border: 4px double #000; border-radius: 8px; padding: 10px 12px; text-align: center; margin: 8px 0 10px; }
    .pickup-banner .huge { font-size: 40px; font-weight: 900; letter-spacing: 4px; font-style: italic; color: #000; }
    .pickup-banner .sub { font-size: 13px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; margin-top: 2px; color: #000; }
    .pickup-banner .icon { font-size: 26px; letter-spacing: 4px; color: #000; }
    table { width: 100%; border-collapse: collapse; font-size: 16px; color: #000; }
    table td { padding: 3px 0; border-bottom: 1px dashed #000; color: #000; line-height: 1.25; }
    /* Whole-order recap — tighter than the per-bag list */
    table.compact { font-size: 13px; }
    table.compact td { padding: 2px 0; }
    .meta { color: #000; font-size: 13px; margin-top: 2px; font-weight: 600; }
    @media print { body { margin: 0; } }
  </style>
  <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
  </head><body>
    <h1>${detail.hotel_name || "—"} <span class="bag-num">Bag ${bundle.bundle_no}/${totalBags}</span></h1>
    <div class="meta">Delivery card · printed ${dateStr}</div>

    ${detail.is_pickup ? `
    <div class="pickup-banner">
      <div class="icon">&#9660; &#9660; &#9660;</div>
      <div class="huge">PICK&nbsp;UP</div>
      <div class="sub">Customer self-collection</div>
      <div class="icon">&#9650; &#9650; &#9650;</div>
    </div>
    ` : ""}
    <h2>Hotel</h2>
    ${hotelAddress ? `<div class="row"><span>Address</span><strong>${hotelAddress}</strong></div>` : ""}

    <h2>Order</h2>
    <div class="row"><span>Order #</span><strong>${detail.order_number}</strong></div>
    <div class="row"><span>Delivery date</span><strong>${detail.delivery_date}</strong></div>

    ${renderBarcodeBlock(bundle.barcode, "bag")}
    <table>${bagItems}</table>
    <div class="big" style="margin-top:4px">${(bundle.items || []).reduce((s, i) => s + (i.quantity || 0), 0)} pcs</div>

    <h2>Whole order — running total</h2>
    <table class="compact">${orderTotals}</table>

    <script>
      window.onload = () => {
        if (window.JsBarcode) {
          document.querySelectorAll('svg.bc').forEach((el) => {
            try { JsBarcode(el, el.getAttribute('jsbarcode-value'), { format:'CODE128', height:48, fontSize:14, lineColor:'#000', width:2, displayValue:true }); } catch (e) { /* fall back to text */ }
          });
        }
        // Fire the print dialog immediately, then close the tab so the
        // operator isn't left with a pile of empty print-preview windows.
        // With Chrome launched in --kiosk-printing mode the dialog is
        // suppressed entirely and the card goes straight to the default
        // printer (see Settings → Integrations → Print Setup for the
        // one-time OS command).
        setTimeout(() => {
          window.print();
          setTimeout(() => window.close(), 400);
        }, 250);
      };
    </script>
  </body></html>`);
  w.document.close();
}
